// Opening move: StressAutoLoadPlugin - Automatic backend stress data loading
// This plugin scans for LyricsTextNodes without stress patterns and fetches from backend
// Big play: Headless detection + debounced batching + deduplication for optimal performance

import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getRoot,
  $getNodeByKey,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  type LexicalNode,
} from 'lexical'
import type { StressCoordinatorService } from '../services/StressCoordinatorService'
import {
  $isLyricsTextNode,
  $getStressPattern,
  type LyricsTextNode,
} from '../nodes/LyricsTextNode'
import { normalizeWordKey, type StressPattern } from '../types/prosody'
import {
  STRESS_AUTOFILL_TAG,
  STRESS_BACKEND_ERROR_TAG,
  STRESS_CONTEXTUAL_TAG,
  SET_STRESS_PATTERN_COMMAND,
} from './StressDataPlugin'

// ============================================================================
// Configuration
// ============================================================================

/**
 * Debounce delay for batching stress data requests
 *
 * Main play: Wait 500ms after typing stops before fetching patterns
 * Victory lap: Prevents spamming backend during rapid typing
 */
const DEBOUNCE_DELAY_MS = 500

// ============================================================================
// Plugin Component
// ============================================================================

export interface StressAutoLoadPluginProps {
  /**
   * StressCoordinatorService instance for analyzing words
   *
   * Main play: DI-friendly design - inject mock for tests, real for production
   * Big play: Same coordinator instance used by StressDataPlugin for cache sharing
   */
  coordinator: StressCoordinatorService
}

/**
 * StressAutoLoadPlugin - Automatically fetches backend stress data as users type
 *
 * Main play: Registers update listener that scans for missing patterns and batches requests
 * Big play: Debounces for 500ms to avoid spamming backend during rapid typing
 * Time to tackle the tricky bit: Deduplicates concurrent requests while updating all target nodes
 *
 * Critical requirements from iteration plan:
 * - MUST register update listener that scans LyricsTextNode instances
 * - MUST debounce for ~500ms before batching requests
 * - MUST use StressCoordinatorService with request deduplication
 * - MUST dispatch SET_STRESS_PATTERN_COMMAND inside editor.update(..., { tag: STRESS_AUTOFILL_TAG })
 * - MUST skip cycles tagged with STRESS_AUTOFILL_TAG or STRESS_BACKEND_ERROR_TAG
 * - MUST log errors with [LEXICAL_STRESS_ERROR] prefix and mirror to window.__LEXICAL_DEBUG__
 * - MUST deduplicate concurrent requests for same word while updating all target nodes
 *
 * Reference: .local_docs/lexical/core-docs/03-commands.md - Update listener patterns
 */
export default function StressAutoLoadPlugin({
  coordinator,
}: StressAutoLoadPluginProps) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    // Opening move: Track pending words and in-flight requests
    // Main play: Map word -> Set<nodeKey> to handle multiple nodes with same word
    // Big play: Set for in-flight words prevents duplicate backend calls
    const pendingWords: Map<string, Set<string>> = new Map()
    const inFlightWords: Set<string> = new Set()
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    /**
     * Process batch of pending words using passage-level analysis
     *
     * Epic: lexical_stressing_logic_scope_01_contextual_passage
     * Main play: Group words by node/line, call analyzePassage once per unique text
     * Time to tackle the tricky bit: Map backend WordAnalysis results back to multiple node instances
     * Victory lap: Single passage call provides contextual stress for all words in line
     */
    const processBatch = async () => {
      // Opening move: Build map of node keys to their text
      // This enables deduplication at the line level instead of word level
      const nodeInfoMap = new Map<string, { text: string }>()

      editor.getEditorState().read(() => {
        // Main play: For each pending word, collect the nodes that need it
        for (const [_word, nodeKeys] of pendingWords.entries()) {
          for (const nodeKey of nodeKeys) {
            try {
              const node = $getNodeByKey(nodeKey)
              if ($isLyricsTextNode(node)) {
                const text = node.getTextContent()
                if (!nodeInfoMap.has(nodeKey)) {
                  nodeInfoMap.set(nodeKey, { text })
                }
              }
            } catch {
              // Node was deleted, skip it
            }
          }
        }
      })

      // Clear pending words now that we've captured them
      pendingWords.clear()

      // Big play: Track in-flight lines by normalized text to prevent duplicate passage calls
      const inFlightLines = new Map<string, Set<string>>() // text -> Set<nodeKey>

      // Time to tackle the tricky bit: Process each unique line text exactly once
      for (const [nodeKey, { text }] of nodeInfoMap.entries()) {
        const normalizedText = text.trim().toLowerCase()

        // Guard: Skip if this line text is already being analyzed
        if (inFlightLines.has(normalizedText)) {
          // Add this node key to the existing entry so we update it when analysis completes
          inFlightLines.get(normalizedText)!.add(nodeKey)
          continue
        }

        // Mark this line as in-flight
        inFlightLines.set(normalizedText, new Set([nodeKey]))

        try {
          // Victory lap: Single passage analysis call provides all word stress patterns
          // Reference: Epic requirement - route every auto-load through passage analysis
          const passageResult = await coordinator.analyzePassage(text)

          // Main play: Convert WordAnalysis results to StressPattern and dispatch updates
          // Big play: Process all target nodes that share this line text
          const affectedNodeKeys = inFlightLines.get(normalizedText)!

          editor.update(
            () => {
              // Time to tackle the tricky bit: For each word analysis from backend
              for (const wordAnalysis of passageResult.words) {
                const normalizedWord = normalizeWordKey(wordAnalysis.word)

                // Victory lap: Create StressPattern from WordAnalysis with reasoning/confidence
                const pattern: StressPattern = {
                  syllables: wordAnalysis.syllables.map((syllableText, index) => ({
                    text: syllableText,
                    stressed: wordAnalysis.stress_pattern[index] > 0,
                    confidence: wordAnalysis.confidence,
                    position: index,
                    overridden: false,
                    reasoning: wordAnalysis.reasoning, // CRITICAL: Capture backend reasoning
                    stressLevel: wordAnalysis.stress_pattern[index] as 0 | 1 | 2, // Epic 06: Three-level stress (0=unstressed, 1=primary, 2=secondary)
                  })),
                  overridden: false,
                  isManualOverride: false, // Auto-applied pattern, not manual
                  overriddenAt: undefined,
                }

                // CRITICAL FIX (iteration_01_a): Only dispatch to nodes that contain this word
                // Problem: Each node typically contains ONE word, so broadcasting to ALL nodes
                // causes ~70% of dispatches to be rejected (word not in node content)
                // Reference: .agent_process/work/.../iteration_01_a/results.md Fix #1
                for (const affectedNodeKey of affectedNodeKeys) {
                  try {
                    const node = $getNodeByKey(affectedNodeKey)
                    if ($isLyricsTextNode(node)) {
                      // Main play: Extract words from this node's content
                      const nodeText = node.getTextContent()
                      const nodeWords = extractWords(nodeText)

                      // DEBUG: Log what we're checking
                      console.log('[StressAutoLoad] Checking node:', {
                        nodeKey: affectedNodeKey,
                        nodeText: nodeText.slice(0, 50),
                        nodeWords,
                        targetWord: normalizedWord,
                        willDispatch: nodeWords.includes(normalizedWord)
                      })

                      // Big play: Only dispatch if this node contains this word
                      if (nodeWords.includes(normalizedWord)) {
                        console.log('[StressAutoLoad] ✅ Dispatching pattern for:', normalizedWord, 'to node:', affectedNodeKey)
                        editor.dispatchCommand(SET_STRESS_PATTERN_COMMAND, {
                          nodeKey: affectedNodeKey,
                          word: normalizedWord,
                          pattern,
                          isAutoFill: true, // Don't mark song as unsaved for auto-loads
                        })
                      } else {
                        console.log('[StressAutoLoad] ❌ Skipping dispatch for:', normalizedWord, 'node doesnt contain it')
                      }
                    }
                  } catch {
                    // Node was deleted between detection and update, skip it
                  }
                }
              }
            },
            { tag: [STRESS_AUTOFILL_TAG, 'history-merge'] } // CRITICAL: 'history-merge' tag makes update non-undoable (bypasses undo stack)
          )
        } catch (error) {
          // Passage analysis failed - log error
          const reason = error instanceof Error ? error.message : 'Unknown error'
          const errorMsg = `[LEXICAL_STRESS_ERROR] Passage analysis failed for "${text.slice(0, 50)}": ${reason}`
          console.error(errorMsg)

          if (typeof window !== 'undefined' && window.__LEXICAL_DEBUG__) {
            window.__LEXICAL_DEBUG__.record?.({
              type: 'stress-passage-analysis-error',
              word: text.slice(0, 50),
              reason,
              timestamp: Date.now(),
            })
          }

          // Tag update to prevent retry loops
          editor.update(() => {
            // No-op update, just tags the failure
          }, { tag: STRESS_BACKEND_ERROR_TAG })
        } finally {
          // Victory lap: Remove from in-flight tracking
          inFlightLines.delete(normalizedText)
        }
      }
    }

    /**
     * Schedule batch processing with debounce
     *
     * Main play: Cancel existing timer and start new one
     * Big play: This ensures we wait 500ms after last editor change before processing
     */
    const scheduleBatch = () => {
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer)
      }
      debounceTimer = setTimeout(() => {
        debounceTimer = null
        processBatch()
      }, DEBOUNCE_DELAY_MS)
    }

    /**
     * Recursively collect all LyricsTextNodes from editor tree
     *
     * Time to tackle the tricky bit: Walk tree using public API (no private _nodeMap access)
     * Reference: .local_docs/lexical/core-docs/01-nodes.md - Tree traversal patterns
     */
    const collectLyricsTextNodes = (node: LexicalNode): LyricsTextNode[] => {
      const textNodes: LyricsTextNode[] = []

      if ($isLyricsTextNode(node)) {
        textNodes.push(node)
      }

      // Recursively collect from children (only ElementNodes have children)
      if ($isElementNode(node)) {
        const children = node.getChildren()
        for (const child of children) {
          textNodes.push(...collectLyricsTextNodes(child))
        }
      }

      return textNodes
    }

    /**
     * Extract words from text content
     *
     * Main play: Split on whitespace and normalize each word
     * Victory lap: Reuses normalizeWordKey helper from LyricsTextNode
     */
    const extractWords = (text: string): string[] => {
      return text
        .split(/\s+/)
        .map(rawWord => normalizeWordKey(rawWord))
        .filter(word => word.length > 0) // Skip pure punctuation
    }

    /**
     * Check if cursor is currently inside a word being typed in the given node
     *
     * Main play: Find cursor position within text and determine which word it's in
     * Big play: Only returns the word if cursor is INSIDE it (not at boundaries)
     * Victory lap: Prevents queueing partial words during active typing
     *
     * @param node - The LyricsTextNode to check
     * @param selection - Current range selection (if any)
     * @returns The normalized word being typed, or null if cursor not inside a word
     */
    const getWordAtCursor = (
      node: LyricsTextNode,
      selection: ReturnType<typeof $getSelection>
    ): string | null => {
      // Guard: Only RangeSelection has cursor position
      if (!$isRangeSelection(selection)) {
        return null
      }

      // Guard: Check if cursor is in this node
      const anchorNode = selection.anchor.getNode()
      if (anchorNode.getKey() !== node.getKey()) {
        return null
      }

      // Main play: Get cursor offset within node text
      const text = node.getTextContent()
      const cursorOffset = selection.anchor.offset

      // Edge case: Cursor at very start or very end means user finished typing that section
      if (cursorOffset === 0 || cursorOffset === text.length) {
        return null
      }

      // Big play: Find word boundaries around cursor
      // Look backwards for start of word (whitespace or start of text)
      let wordStart = cursorOffset
      while (wordStart > 0 && !/\s/.test(text[wordStart - 1])) {
        wordStart--
      }

      // Look forwards for end of word (whitespace or end of text)
      let wordEnd = cursorOffset
      while (wordEnd < text.length && !/\s/.test(text[wordEnd])) {
        wordEnd++
      }

      // Time to tackle the tricky bit: Extract and normalize the word at cursor
      const rawWord = text.slice(wordStart, wordEnd).trim()
      if (!rawWord) {
        return null
      }

      const normalizedWord = normalizeWordKey(rawWord)

      // Victory lap: Return the word user is actively typing
      return normalizedWord.length > 0 ? normalizedWord : null
    }

    // ========================================================================
    // Update Listener Registration
    // ========================================================================

    const removeUpdateListener = editor.registerUpdateListener(({ editorState, tags }) => {
      // CRITICAL: Guard against listener loops - skip if update came from auto-load, contextual, or backend error
      if (tags.has(STRESS_AUTOFILL_TAG) || tags.has(STRESS_CONTEXTUAL_TAG) || tags.has(STRESS_BACKEND_ERROR_TAG)) {
        return
      }

      // Main play: Scan for LyricsTextNodes that might need analysis
      editorState.read(() => {
        const root = $getRoot()
        const textNodes = collectLyricsTextNodes(root)
        const selection = $getSelection()

        // Big play: For each text node, extract words and check if they need analysis
        for (const node of textNodes) {
          const text = node.getTextContent()
          if (!text.trim()) continue // Skip empty text nodes

          const words = extractWords(text)

          // Critical fix: Detect which word user is actively typing (if any)
          // Victory lap: Skip this word to prevent queueing partial content like "h", "he", "hel"
          const wordAtCursor = getWordAtCursor(node, selection)

          for (const word of words) {
            // Time to tackle the tricky bit: Check if this word already has stress data
            const existingPattern = $getStressPattern(node, word)

            // Epic 05 iteration_01_c fix: Skip word being actively typed
            // Main play: Only queue complete words (those NOT under the cursor)
            const isBeingTyped = word === wordAtCursor

            // Critical fix: Check if word is already pending OR in-flight to prevent debounce reset
            if (!existingPattern && !inFlightWords.has(word) && !pendingWords.has(word) && !isBeingTyped) {
              // Victory lap: No pattern exists and not already pending/fetching, add to pending batch
              pendingWords.set(word, new Set())
              pendingWords.get(word)!.add(node.getKey())
            } else if (!existingPattern && pendingWords.has(word) && !isBeingTyped) {
              // Word is already pending - just add this node key to the existing entry
              pendingWords.get(word)!.add(node.getKey())
            }
          }
        }
      })

      // Big play: Schedule batch processing if we have pending work
      if (pendingWords.size > 0) {
        scheduleBatch()
      }
    })

    // ========================================================================
    // Cleanup
    // ========================================================================

    return () => {
      removeUpdateListener()
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer)
      }
      // Clear pending state
      pendingWords.clear()
      inFlightWords.clear()
    }
  }, [editor, coordinator])

  // Victory lap: Headless plugin returns null (no UI)
  return null
}

// Victory lap: StressAutoLoadPlugin complete with detection, debouncing, and error handling
// Scans for missing patterns, batches backend calls, updates all target nodes, prevents loops
// Ready for integration with baseline editor and comprehensive testing
